<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { api, type AdminUserRecord, type SwitchableUser } from '../api/client'
import FundAccessGrid from './FundAccessGrid.vue'
import CharterisAdminStatusCard from './charteris/CharterisAdminStatusCard.vue'
import { useAuth } from '../composables/useAuth'

// Charteris Phase 2.5 Task 6, fix round 1 (review finding, Important): the
// status card is gated on isSuperUser ALONE — defence in depth, since
// Dashboard.vue already gates the whole Admin tab to a superuser. It is
// deliberately NOT ANDed with an entitlement check: the backend endpoint
// (CharterisAdminStatusEndpoints.cs) is superuser-only and 404s for
// everyone else regardless of entitlement, so gating this card on
// useFundEnablement().isFundVisible('CHARTERIS') (entitledFunds-only, no
// superuser bypass — useFundEnablement.ts) made the card permanently
// invisible to everyone, including the superuser it exists for, since no
// CHARTERIS user_fund_entitlements grant exists yet
// (docs/runbooks/charteris-fund-dark.md). Used directly in the template
// (not `.value`'d in script) so a real computed ref is auto-unwrapped by
// the template the same way a test double's plain boolean passes through
// unchanged.
const { isSuperUser } = useAuth()

// Fix (linked users, HL user ID correction): a linked user's HL user ID is
// the name she signs in to HL with (e.g. "Naty2018"), NOT her numeric HL
// account/client number — that mixup is exactly why the admin HL connect
// 401'd for Paolo's daughters. Applied to both the "Add linked user" input
// and the edit input; it's a warning only, since HL is not guaranteed to
// never allow a numeric user ID.
function looksLikeHlAccountNumber(value: string): boolean {
  const v = value.trim()
  return v.length > 0 && /^[0-9]+$/.test(v)
}

const users = ref<AdminUserRecord[]>([])
const jobs = ref<any[]>([])
const loadingUsers = ref(true)
const loadingJobs = ref(true)
const newEmail = ref('')
const createError = ref<string | null>(null)
const createSuccess = ref<string | null>(null)
const creating = ref(false)

// Task 6 (spec 2026-09-28 linked users): switcher list + "add linked user" form.
const switchableUsers = ref<SwitchableUser[]>([])
// Only the caller's OWN linked users belong in this card: it exists to
// correct their HL user ID. Switching itself happens in the header
// UserSwitcher, so listing everyone (the caller included) here only
// duplicated rows elsewhere on the Admin page.
const myLinkedUsers = computed(() => switchableUsers.value.filter(u => u.isLinked && u.isMine))
const loadingSwitchableUsers = ref(true)
const linkedHlUsername = ref('')
const linkedDisplayName = ref('')
const linkedCreateError = ref<string | null>(null)
const linkedCreateSuccess = ref<string | null>(null)
const creatingLinkedUser = ref(false)
const linkedHlUsernameLooksNumeric = computed(() => looksLikeHlAccountNumber(linkedHlUsername.value))

// Fix (linked users, HL user ID correction): inline "Edit HL user ID"
// action on the switcher list, for the caller's own linked users only
// (su.isMine, set server-side from the same guardian check the endpoint
// enforces).
const editingHlUsernameUserId = ref<string | null>(null)
const editHlUsernameDraft = ref('')
const editHlUsernameError = ref<string | null>(null)
const savingHlUsernameUserId = ref<string | null>(null)
const editHlUsernameLooksNumeric = computed(() => looksLikeHlAccountNumber(editHlUsernameDraft.value))

function startEditHlUsername(su: SwitchableUser) {
  editingHlUsernameUserId.value = su.userId
  editHlUsernameDraft.value = su.hlUsername ?? ''
  editHlUsernameError.value = null
}

function cancelEditHlUsername() {
  editingHlUsernameUserId.value = null
  editHlUsernameDraft.value = ''
  editHlUsernameError.value = null
}

async function saveHlUsername(su: SwitchableUser) {
  editHlUsernameError.value = null
  const value = editHlUsernameDraft.value.trim()
  if (!value) {
    editHlUsernameError.value = 'HL user ID is required'
    return
  }
  savingHlUsernameUserId.value = su.userId
  try {
    await api.adminUpdateLinkedUserHlUsername(su.userId, value)
    editingHlUsernameUserId.value = null
    editHlUsernameDraft.value = ''
    await loadData()
  } catch (e: any) {
    editHlUsernameError.value = e.message ?? 'Failed to update HL user ID'
  } finally {
    savingHlUsernameUserId.value = null
  }
}

async function loadData() {
  loadingUsers.value = true
  loadingJobs.value = true
  loadingSwitchableUsers.value = true
  try {
    users.value = await api.adminUsers()
  } catch (e: any) {
    console.error('Failed to load admin users', e)
  } finally {
    loadingUsers.value = false
  }
  try {
    jobs.value = await api.adminJobs(50)
  } catch (e: any) {
    console.error('Failed to load admin jobs', e)
  } finally {
    loadingJobs.value = false
  }
  try {
    switchableUsers.value = await api.adminSwitchableUsers()
  } catch (e: any) {
    console.error('Failed to load switchable users', e)
  } finally {
    loadingSwitchableUsers.value = false
  }
}

async function createLinkedUser() {
  linkedCreateError.value = null
  linkedCreateSuccess.value = null
  const hlUsername = linkedHlUsername.value.trim()
  const displayName = linkedDisplayName.value.trim()
  if (!hlUsername) {
    linkedCreateError.value = 'HL user ID is required'
    return
  }
  if (!displayName) {
    linkedCreateError.value = 'Name is required'
    return
  }
  if (displayName.length > 40) {
    linkedCreateError.value = 'Name must be 40 characters or fewer'
    return
  }
  creatingLinkedUser.value = true
  try {
    const created = await api.adminCreateLinkedUser(hlUsername, displayName)
    linkedCreateSuccess.value = `Linked user ${created.displayName} created successfully`
    linkedHlUsername.value = ''
    linkedDisplayName.value = ''
    await loadData()
  } catch (e: any) {
    linkedCreateError.value = e.message ?? 'Failed to create linked user'
  } finally {
    creatingLinkedUser.value = false
  }
}

async function createUser() {
  createError.value = null
  createSuccess.value = null
  if (!newEmail.value.trim()) {
    createError.value = 'Email is required'
    return
  }
  creating.value = true
  try {
    const created = await api.adminCreateUser(newEmail.value.trim())
    createSuccess.value = `User ${created.email} created successfully`
    newEmail.value = ''
    await loadData()
  } catch (e: any) {
    createError.value = e.message ?? 'Failed to create user'
  } finally {
    creating.value = false
  }
}

async function deactivateUser(user: AdminUserRecord) {
  if (!confirm(`Deactivate ${user.email}? This will prevent them from logging in.`)) return
  try {
    await api.adminDeactivateUser(user.userId)
    await loadData()
  } catch (e: any) {
    alert(`Failed to deactivate: ${e.message ?? 'Unknown error'}`)
  }
}

const reconcilingUserId = ref<string | null>(null)

async function reconcileUser(user: AdminUserRecord) {
  if (!confirm(`Enqueue a reconciliation job for ${user.email}? This will scrape HL and backfill any missing trades into the subledger.`)) return
  reconcilingUserId.value = user.userId
  try {
    await api.adminReconcileUser(user.userId)
    await loadData()
  } catch (e: any) {
    alert(`Failed to reconcile: ${e.message ?? 'Unknown error'}`)
  } finally {
    reconcilingUserId.value = null
  }
}

// Per-user WhatsApp routing (2026-09-28): account alerts go to each user's
// own number. The server only returns hasWhatsapp + a masked number; the
// full number is typed here, saved, and never read back.
const waDraft = ref<Record<string, string>>({})
const waError = ref<Record<string, string | null>>({})
const waSavingUserId = ref<string | null>(null)

// A linked account (guardianUserId set) never has a WhatsApp of her own —
// her alerts always go to her guardian's number (2026-09-30), so the row
// shows whose WhatsApp she uses and offers no input.
function isLinkedUser(u: AdminUserRecord): boolean {
  return !!u.guardianUserId && u.guardianUserId !== u.userId
}
function guardianWhatsappLabel(u: AdminUserRecord): string {
  const g = users.value.find(x => x.userId === u.guardianUserId)
  const who = g?.email ?? 'guardian'
  return g?.hasWhatsapp ? `uses ${who}'s (${g.whatsappNumberMasked})` : `uses ${who}'s — not set`
}

async function saveWhatsapp(user: AdminUserRecord, number: string | null) {
  waError.value = { ...waError.value, [user.userId]: null }
  if (number !== null && !number.trim()) {
    waError.value = { ...waError.value, [user.userId]: 'Enter a number (e.g. 447700900123), or use Clear' }
    return
  }
  waSavingUserId.value = user.userId
  try {
    await api.adminSetUserWhatsapp(user.userId, number === null ? null : number.trim())
    waDraft.value = { ...waDraft.value, [user.userId]: '' }
    await loadData()
  } catch (e: any) {
    waError.value = { ...waError.value, [user.userId]: e?.message ?? 'Failed to save WhatsApp number' }
  } finally {
    waSavingUserId.value = null
  }
}

const manualClosingUserId = ref<string | null>(null)
const showCloseModalFor = ref<AdminUserRecord | null>(null)
const closeForm = ref<{ instrument: 'LQQ3' | 'LQQS'; exitPriceGbp: string }>({
  instrument: 'LQQ3',
  exitPriceGbp: '',
})
const modalBackdropEl = ref<HTMLDivElement | null>(null)
watch(showCloseModalFor, async (v) => {
  if (v) {
    await nextTick()
    modalBackdropEl.value?.focus()
  }
})

async function submitManualClose(user: AdminUserRecord) {
  const price = Number(closeForm.value.exitPriceGbp)
  if (!price || price <= 0) {
    alert('Exit price must be a positive number (GBp/unit).')
    return
  }
  if (!confirm(
    `Write manual EXIT row for ${user.email} — ${closeForm.value.instrument} @ ${price} GBp/unit?\n\n` +
    'This writes the EXIT row in hedge_trades, clears the OTP active-position Redis cache, and enqueues reconciliation.\n' +
    'Use only after OtpCloseExecutor returned "Unknown — manual check required".',
  )) return
  manualClosingUserId.value = user.userId
  try {
    const result = await api.adminOtpManualClose(user.userId, closeForm.value.instrument, { exitPriceGbp: price })
    alert(
      `Manual-close complete for ${user.email}:\n` +
      `  Hedge trade ID : ${result.hedgeTradeId}\n` +
      `  Units closed   : ${result.unitsClosed}\n` +
      `  Exit price     : ${result.exitPriceGbp} GBp\n` +
      `  Net P&L        : £${result.netPnlGbp.toFixed(2)}\n` +
      `  Commission     : £${result.commissionGbp.toFixed(2)}\n` +
      `  Redis key found: ${result.redisKeyExisted}\n` +
      `  Recon job ID   : ${result.reconciliationJobId}`,
    )
    showCloseModalFor.value = null
    closeForm.value = { instrument: 'LQQ3', exitPriceGbp: '' }
    await loadData()
  } catch (e: any) {
    alert(`Manual-close failed: ${e.message ?? 'Unknown error'}`)
  } finally {
    manualClosingUserId.value = null
  }
}

const jhgtlRefreshing = ref(false)
const jhgtlResult = ref<{
  newRows: number
  skippedTickers: string[]
  errors: Array<{ ticker: string; error: string }>
  tickersProcessed: number
} | null>(null)
const jhgtlError = ref<string | null>(null)

async function onRefreshJhgtl() {
  jhgtlRefreshing.value = true
  jhgtlError.value = null
  try {
    jhgtlResult.value = await api.adminJhgtlForceRefresh()
  } catch (e) {
    jhgtlError.value = e instanceof Error ? e.message : String(e)
  } finally {
    jhgtlRefreshing.value = false
  }
}

function credBadgeClass(status: string): string {
  if (status === 'valid') return 'badge-green'
  if (status === 'invalid') return 'badge-red'
  return 'badge-grey'
}

function jobStatusClass(status: string): string {
  if (status === 'completed') return 'badge-green'
  if (status === 'failed') return 'badge-red'
  if (status === 'skipped') return 'badge-orange'
  if (status === 'running') return 'badge-blue'
  return 'badge-grey'
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return iso
  }
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  } catch {
    return iso
  }
}

onMounted(loadData)

// Spec 2026-09-30 §8: sub-tabs Users (today's content, unchanged) and Fund access, kept in the URL
// hash as #admin/users | #admin/fund-access (Dashboard keeps the admin/ sub-hash intact).
type AdminSubTab = 'users' | 'fund-access'
function subTabFromHash(): AdminSubTab {
  return window.location.hash.replace('#', '') === 'admin/fund-access' ? 'fund-access' : 'users'
}
const subTab = ref<AdminSubTab>(subTabFromHash())
function selectSubTab(t: AdminSubTab) {
  subTab.value = t
  window.location.hash = `admin/${t}`
}
function onAdminHashChange() {
  if (window.location.hash.startsWith('#admin')) subTab.value = subTabFromHash()
}
onMounted(() => window.addEventListener('hashchange', onAdminHashChange))
onUnmounted(() => window.removeEventListener('hashchange', onAdminHashChange))
</script>

<template>
  <div class="admin-panel">
    <nav class="admin-subtabs" role="tablist" data-testid="admin-subtabs">
      <button type="button" role="tab" :aria-selected="subTab === 'users'"
              :class="['admin-subtab', { active: subTab === 'users' }]"
              data-testid="admin-subtab-users" @click="selectSubTab('users')">Users</button>
      <button type="button" role="tab" :aria-selected="subTab === 'fund-access'"
              :class="['admin-subtab', { active: subTab === 'fund-access' }]"
              data-testid="admin-subtab-fund-access" @click="selectSubTab('fund-access')">Fund access</button>
    </nav>

    <FundAccessGrid v-if="subTab === 'fund-access'" />

    <template v-else>
    <!-- Create User Card -->
    <div class="card">
      <div class="card-header">Create User</div>
      <div class="card-body">
        <div class="create-form">
          <input
            v-model="newEmail"
            type="email"
            placeholder="user@example.com"
            class="email-input"
            @keyup.enter="createUser"
            :disabled="creating"
          />
          <button class="btn-primary" @click="createUser" :disabled="creating">
            {{ creating ? 'Creating…' : 'Create' }}
          </button>
        </div>
        <div v-if="createSuccess" class="msg-success">{{ createSuccess }}</div>
        <div v-if="createError" class="msg-error">{{ createError }}</div>
      </div>
    </div>

    <!-- Add Linked User Card (Task 6, spec 2026-09-28 linked users) -->
    <div class="card">
      <div class="card-header">Add linked user</div>
      <div class="card-body">
        <div class="field-group">
          <label class="field-label" for="linked-hl-username">HL user ID (the name she signs in to HL with)</label>
          <div class="create-form">
            <input
              id="linked-hl-username"
              v-model="linkedHlUsername"
              type="text"
              placeholder="e.g. Naty2018"
              class="email-input"
              data-testid="linked-hl-username"
              @keyup.enter="createLinkedUser"
              :disabled="creatingLinkedUser"
            />
            <input
              v-model="linkedDisplayName"
              type="text"
              placeholder="Name"
              class="email-input"
              data-testid="linked-display-name"
              maxlength="40"
              @keyup.enter="createLinkedUser"
              :disabled="creatingLinkedUser"
            />
            <button
              class="btn-primary"
              data-testid="linked-create"
              @click="createLinkedUser"
              :disabled="creatingLinkedUser"
            >
              {{ creatingLinkedUser ? 'Adding…' : 'Add' }}
            </button>
          </div>
          <div v-if="linkedHlUsernameLooksNumeric" class="msg-warning" data-testid="linked-hl-username-warning">
            That looks like an HL account/client number — use the HL login user ID instead.
          </div>
        </div>
        <div v-if="linkedCreateSuccess" class="msg-success" data-testid="linked-create-success">{{ linkedCreateSuccess }}</div>
        <div v-if="linkedCreateError" class="msg-error" data-testid="linked-create-error">{{ linkedCreateError }}</div>
      </div>
    </div>

    <!-- Switcher List Card (Task 6): who a superuser may view as -->
    <div class="card">
      <div class="card-header">
        Your linked users
        <span class="header-count">{{ myLinkedUsers.length }}</span>
      </div>
      <div class="card-body">
        <div v-if="loadingSwitchableUsers" class="loading-text">Loading linked users…</div>
        <div v-else-if="myLinkedUsers.length === 0" class="empty-text">No linked users yet</div>
        <div v-else class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Linked</th>
                <th>HL user ID</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="su in myLinkedUsers" :key="su.userId" :data-testid="`switchable-${su.userId}`">
                <td>{{ su.label }}</td>
                <td>{{ su.role }}</td>
                <td>{{ su.isLinked ? 'Yes' : 'No' }}</td>
                <td class="mono-cell">
                  <div v-if="editingHlUsernameUserId === su.userId" class="field-group">
                    <label class="field-label" :for="`edit-hl-username-${su.userId}`">
                      HL user ID (the name she signs in to HL with)
                    </label>
                    <div class="edit-hl-row">
                      <input
                        :id="`edit-hl-username-${su.userId}`"
                        v-model="editHlUsernameDraft"
                        type="text"
                        placeholder="e.g. Naty2018"
                        class="edit-hl-input"
                        data-testid="edit-hl-username-input"
                        :disabled="savingHlUsernameUserId === su.userId"
                        @keyup.enter="saveHlUsername(su)"
                      />
                      <button
                        class="btn-touch"
                        data-testid="edit-hl-username-save"
                        :disabled="savingHlUsernameUserId === su.userId"
                        @click="saveHlUsername(su)"
                      >
                        {{ savingHlUsernameUserId === su.userId ? '…' : 'Save' }}
                      </button>
                      <button
                        class="btn-touch btn-touch-cancel"
                        data-testid="edit-hl-username-cancel"
                        :disabled="savingHlUsernameUserId === su.userId"
                        @click="cancelEditHlUsername"
                      >
                        Cancel
                      </button>
                    </div>
                    <div class="sub-text">Changing it signs her out of HL — reconnect her afterwards</div>
                    <div v-if="editHlUsernameLooksNumeric" class="msg-warning" data-testid="edit-hl-username-warning">
                      That looks like an HL account/client number — use the HL login user ID instead.
                    </div>
                    <div v-if="editHlUsernameError" class="msg-error" data-testid="edit-hl-username-error">{{ editHlUsernameError }}</div>
                  </div>
                  <template v-else>{{ su.hlUsername ?? '—' }}</template>
                </td>
                <td class="action-cell">
                  <button
                    v-if="su.isLinked && su.isMine && editingHlUsernameUserId !== su.userId"
                    class="btn-touch"
                    data-testid="edit-hl-username-start"
                    @click="startEditHlUsername(su)"
                  >
                    Edit HL user ID
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Users Table Card -->
    <div class="card">
      <div class="card-header">
        Users
        <span class="header-count">{{ users.length }}</span>
      </div>
      <div class="card-body">
        <div v-if="loadingUsers" class="loading-text">Loading users…</div>
        <div v-else-if="users.length === 0" class="empty-text">No users found</div>
        <div v-else class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Credentials</th>
                <th>Flags</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="u in users" :key="u.userId">
                <td>
                  <div class="email-cell">
                    {{ u.email }}
                    <span v-if="u.isUserOfLastResort" class="badge badge-purple">System Admin</span>
                    <span v-if="!u.isActive" class="badge badge-grey">Inactive</span>
                  </div>
                  <div v-if="isLinkedUser(u)" class="wa-block" :data-testid="`whatsapp-${u.userId}`">
                    <div class="sub-text" data-testid="whatsapp-guardian">
                      WhatsApp:
                      <span class="mono-cell">{{ guardianWhatsappLabel(u) }}</span>
                    </div>
                  </div>
                  <div v-else class="wa-block" :data-testid="`whatsapp-${u.userId}`">
                    <div class="sub-text">
                      WhatsApp:
                      <span class="mono-cell">{{ u.hasWhatsapp ? u.whatsappNumberMasked : 'not set' }}</span>
                    </div>
                    <div class="wa-edit">
                      <input
                        v-model="waDraft[u.userId]"
                        type="tel"
                        inputmode="tel"
                        autocomplete="off"
                        placeholder="447700900123"
                        class="wa-input"
                        :aria-label="`WhatsApp number for ${u.email}`"
                        :disabled="waSavingUserId === u.userId"
                        @keyup.enter="saveWhatsapp(u, waDraft[u.userId] ?? '')"
                      />
                      <button
                        class="btn-secondary-sm"
                        data-testid="whatsapp-save"
                        :disabled="waSavingUserId === u.userId"
                        @click="saveWhatsapp(u, waDraft[u.userId] ?? '')"
                      >
                        {{ waSavingUserId === u.userId ? '…' : 'Save' }}
                      </button>
                      <button
                        v-if="u.hasWhatsapp"
                        class="btn-danger-sm"
                        data-testid="whatsapp-clear"
                        :disabled="waSavingUserId === u.userId"
                        @click="saveWhatsapp(u, null)"
                      >
                        Clear
                      </button>
                    </div>
                    <div v-if="waError[u.userId]" class="wa-error">{{ waError[u.userId] }}</div>
                  </div>
                </td>
                <td>
                  <span class="badge" :class="credBadgeClass(u.credentialsStatus)">
                    {{ u.credentialsStatus.replace('_', ' ') }}
                  </span>
                  <div v-if="u.credentialsInvalidSince" class="sub-text">
                    since {{ fmtDate(u.credentialsInvalidSince) }}
                  </div>
                </td>
                <td>
                  <div class="flags">
                    <span v-if="u.isSuperUser" class="badge badge-blue">superuser</span>
                    <span v-if="u.isBillable" class="badge badge-grey">billable</span>
                  </div>
                </td>
                <td class="date-cell">{{ fmtDate(u.createdAt) }}</td>
                <td class="action-cell">
                  <button
                    v-if="u.isActive"
                    class="btn-secondary-sm"
                    :disabled="reconcilingUserId === u.userId"
                    @click="reconcileUser(u)"
                    title="Scrape HL and backfill any missing trades into the subledger"
                  >
                    {{ reconcilingUserId === u.userId ? '…' : 'Reconcile' }}
                  </button>
                  <button
                    v-if="u.isActive"
                    class="btn-secondary-sm"
                    :disabled="manualClosingUserId === u.userId"
                    @click="showCloseModalFor = u"
                    title="Write missing EXIT row + clear OTP cache after an ambiguous OtpCloseExecutor failure"
                  >
                    Manual-close OTP
                  </button>
                  <button
                    v-if="!u.isUserOfLastResort && u.isActive"
                    class="btn-danger-sm"
                    @click="deactivateUser(u)"
                  >
                    Deactivate
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- JH Holdings Refresh Card -->
    <div class="card">
      <div class="card-header">JH Holdings</div>
      <div class="card-body">
        <button class="btn-primary" @click="onRefreshJhgtl" :disabled="jhgtlRefreshing">
          {{ jhgtlRefreshing ? 'Refreshing…' : 'Refresh JH holdings (AlphaVantage backfill)' }}
        </button>
        <div v-if="jhgtlResult" class="msg-success">
          Added {{ jhgtlResult.newRows }} rows across {{ jhgtlResult.tickersProcessed }} tickers;
          skipped {{ jhgtlResult.skippedTickers.length }}; errors {{ jhgtlResult.errors.length }}.
        </div>
        <div v-if="jhgtlError" class="msg-error">Error: {{ jhgtlError }}</div>
      </div>
    </div>

    <!-- Charteris status card (Phase 2.5 Task 6): superuser-only, no entitlement condition
         (fix round 1 — see the comment at this component's top for why) -->
    <CharterisAdminStatusCard v-if="isSuperUser" />

    <!-- Jobs Table Card -->
    <div class="card">
      <div class="card-header">
        Job Health
        <span class="header-count">last 50</span>
      </div>
      <div class="card-body">
        <div v-if="loadingJobs" class="loading-text">Loading jobs…</div>
        <div v-else-if="jobs.length === 0" class="empty-text">No jobs found</div>
        <div v-else class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Status</th>
                <th>Attempt</th>
                <th>Scheduled</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(job, idx) in jobs" :key="idx">
                <td class="mono-cell">{{ job.jobType ?? job.type ?? '—' }}</td>
                <td>
                  <span class="badge" :class="jobStatusClass(job.status)">
                    {{ job.status }}
                  </span>
                </td>
                <td class="mono-cell">{{ job.attemptNumber ?? job.attempt ?? '—' }}</td>
                <td class="date-cell">{{ fmtDateTime(job.scheduledAt ?? job.createdAt ?? null) }}</td>
                <td class="error-cell">{{ job.errorMessage ?? job.error ?? '' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Manual-close OTP modal -->
    <div
      v-if="showCloseModalFor"
      class="modal-backdrop"
      ref="modalBackdropEl"
      tabindex="-1"
      @click.self="showCloseModalFor = null"
      @keydown.esc="showCloseModalFor = null"
    >
      <div class="modal">
        <h3 class="modal-title">Manual-close OTP for {{ showCloseModalFor.email }}</h3>
        <p class="modal-help">
          Writes the EXIT row in hedge_trades, enqueues reconciliation,
          and clears the OTP active-position cache. Use only after
          OtpCloseExecutor returned "Unknown — manual check required".
        </p>
        <div class="modal-field">
          <label class="modal-label">Instrument</label>
          <select v-model="closeForm.instrument" class="modal-select">
            <option value="LQQ3">LQQ3</option>
            <option value="LQQS">LQQS</option>
          </select>
        </div>
        <div class="modal-field">
          <label class="modal-label">Exit price (GBp/unit)</label>
          <input
            v-model="closeForm.exitPriceGbp"
            type="number"
            step="0.001"
            min="0"
            placeholder="e.g. 8432.500"
            class="modal-input"
          />
        </div>
        <div class="modal-actions">
          <button
              class="btn-secondary-sm"
              :disabled="manualClosingUserId === showCloseModalFor?.userId"
              @click="showCloseModalFor = null"
            >Cancel</button>
          <button
            class="btn-secondary-sm"
            :disabled="manualClosingUserId === showCloseModalFor.userId"
            @click="submitManualClose(showCloseModalFor)"
          >
            {{ manualClosingUserId === showCloseModalFor.userId ? '…' : 'Submit' }}
          </button>
        </div>
      </div>
    </div>
    </template>

  </div>
</template>

<style scoped>
.admin-subtabs { display: flex; gap: 0.5rem; border-bottom: 1px solid #21262d; }
.admin-subtab { min-height: 44px; padding: 0 1rem; background: none; color: #8b949e; border: none; border-bottom: 2px solid transparent; cursor: pointer; }
.admin-subtab.active { color: #c9d1d9; border-bottom-color: #58a6ff; }
.admin-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  color: #c9d1d9;
  font-family: system-ui, -apple-system, sans-serif;
}

.card {
  background: #161b22;
  border: 1px solid #21262d;
  border-radius: 8px;
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: #1c2128;
  border-bottom: 1px solid #21262d;
  font-size: 0.85rem;
  font-weight: 600;
  color: #e6edf3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.header-count {
  font-size: 0.75rem;
  font-weight: 400;
  color: #6e7681;
  background: #21262d;
  padding: 1px 6px;
  border-radius: 10px;
}

.card-body {
  padding: 0.75rem 1rem;
}

/* Create form */
.create-form {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.email-input {
  flex: 1;
  padding: 0.45rem 0.75rem;
  background: #0d1117;
  border: 1px solid #30363d;
  border-radius: 4px;
  color: #c9d1d9;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.15s;
}
.email-input:focus { border-color: #58a6ff; }
.email-input::placeholder { color: #484f58; }

.btn-primary {
  padding: 0.45rem 1rem;
  background: #238636;
  border: 1px solid #2ea043;
  border-radius: 4px;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}
.btn-primary:hover:not(:disabled) { background: #2ea043; }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

.msg-success {
  margin-top: 0.5rem;
  padding: 0.4rem 0.75rem;
  background: rgba(63,185,80,0.1);
  border: 1px solid #238636;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #3fb950;
}

.msg-error {
  margin-top: 0.5rem;
  padding: 0.4rem 0.75rem;
  background: rgba(248,81,73,0.1);
  border: 1px solid #f85149;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #f85149;
}

/* Tables */
.table-wrap {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.data-table th {
  text-align: left;
  padding: 6px 10px;
  font-size: 0.7rem;
  color: #6e7681;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #21262d;
  white-space: nowrap;
}

.data-table td {
  padding: 7px 10px;
  border-bottom: 1px solid #161b22;
  vertical-align: middle;
  color: #c9d1d9;
}

.data-table tbody tr:hover { background: #1c2128; }
.data-table tbody tr:last-child td { border-bottom: none; }

/* Badges */
.badge {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 3px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: lowercase;
}

.badge-green { background: rgba(63,185,80,0.15); color: #3fb950; border: 1px solid rgba(63,185,80,0.3); }
.badge-red { background: rgba(248,81,73,0.15); color: #f85149; border: 1px solid rgba(248,81,73,0.3); }
.badge-grey { background: rgba(110,118,129,0.15); color: #6e7681; border: 1px solid #30363d; }
.badge-orange { background: rgba(210,153,34,0.15); color: #d29922; border: 1px solid rgba(210,153,34,0.3); }
.badge-blue { background: rgba(88,166,255,0.15); color: #58a6ff; border: 1px solid rgba(88,166,255,0.3); }
.badge-purple { background: rgba(163,113,247,0.15); color: #a371f7; border: 1px solid rgba(163,113,247,0.3); }

/* Cell helpers */
.email-cell {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

/* Per-user WhatsApp number (2026-09-28) */
.wa-block {
  margin-top: 4px;
}

.wa-edit {
  display: flex;
  gap: 0.3rem;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 3px;
}

.wa-input {
  width: 11rem;
  max-width: 100%;
  padding: 3px 6px;
  background: #0d1117;
  border: 1px solid #30363d;
  border-radius: 4px;
  color: #c9d1d9;
  font-size: 0.8rem;
  outline: none;
  transition: border-color 0.15s;
}
.wa-input:focus { border-color: #58a6ff; }
.wa-input::placeholder { color: #484f58; }
.wa-edit .btn-secondary-sm { margin-right: 0; }

.wa-error {
  margin-top: 2px;
  font-size: 0.72rem;
  color: #f85149;
}

.flags {
  display: flex;
  gap: 0.3rem;
  flex-wrap: wrap;
}

.sub-text {
  font-size: 0.7rem;
  color: #6e7681;
  margin-top: 2px;
}

.date-cell {
  white-space: nowrap;
  font-size: 0.8rem;
  color: #8b949e;
}

.mono-cell {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
}

.action-cell { text-align: right; }

.error-cell {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.78rem;
  color: #f85149;
}

.btn-danger-sm {
  padding: 3px 10px;
  background: transparent;
  border: 1px solid #f85149;
  border-radius: 4px;
  color: #f85149;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.btn-danger-sm:hover {
  background: rgba(248,81,73,0.15);
}

.btn-secondary-sm {
  padding: 3px 10px;
  margin-right: 0.4rem;
  background: transparent;
  border: 1px solid #58a6ff;
  border-radius: 4px;
  color: #58a6ff;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.btn-secondary-sm:hover:not(:disabled) {
  background: rgba(88,166,255,0.15);
}
.btn-secondary-sm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Fix (linked users, HL user ID correction) */
.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.field-label {
  font-size: 0.78rem;
  color: #6e7681;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.msg-warning {
  margin-top: 0.35rem;
  padding: 0.35rem 0.6rem;
  background: rgba(210, 153, 34, 0.12);
  border: 1px solid #d29922;
  border-radius: 4px;
  font-size: 0.78rem;
  color: #d29922;
}

.edit-hl-row {
  display: flex;
  gap: 0.4rem;
  align-items: center;
  flex-wrap: wrap;
}

.edit-hl-input {
  flex: 1;
  min-width: 8rem;
  min-height: 44px;
  padding: 0.5rem 0.75rem;
  background: #0d1117;
  border: 1px solid #30363d;
  border-radius: 4px;
  color: #c9d1d9;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.15s;
}
.edit-hl-input:focus { border-color: #58a6ff; }
.edit-hl-input::placeholder { color: #484f58; }

/* Mobile-first touch target (≥44px) for the HL user ID edit action. */
.btn-touch {
  min-height: 44px;
  min-width: 44px;
  padding: 0.4rem 0.9rem;
  background: transparent;
  border: 1px solid #58a6ff;
  border-radius: 4px;
  color: #58a6ff;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.btn-touch:hover:not(:disabled) { background: rgba(88, 166, 255, 0.15); }
.btn-touch:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-touch-cancel { border-color: #6e7681; color: #8b949e; }
.btn-touch-cancel:hover:not(:disabled) { background: rgba(110, 118, 129, 0.15); }

.loading-text, .empty-text {
  padding: 0.75rem 0;
  color: #6e7681;
  font-size: 0.9rem;
  font-style: italic;
}

/* Modal */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 8px;
  padding: 1.25rem 1.5rem;
  width: min(480px, 90vw);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.modal-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #e6edf3;
}

.modal-help {
  margin: 0;
  font-size: 0.8rem;
  color: #8b949e;
  line-height: 1.5;
  border-left: 3px solid #d29922;
  padding-left: 0.6rem;
}

.modal-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.modal-label {
  font-size: 0.78rem;
  color: #6e7681;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.modal-select,
.modal-input {
  padding: 0.4rem 0.6rem;
  background: #0d1117;
  border: 1px solid #30363d;
  border-radius: 4px;
  color: #c9d1d9;
  font-size: 0.88rem;
  outline: none;
  transition: border-color 0.15s;
}
.modal-select:focus,
.modal-input:focus { border-color: #58a6ff; }
.modal-input::placeholder { color: #484f58; }

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

@media (max-width: 600px) {
  .create-form { flex-direction: column; align-items: stretch; }
  .data-table th, .data-table td { padding: 5px 6px; }
  .wa-input { width: 100%; flex: 1 1 8rem; }
  .edit-hl-row { flex-direction: column; align-items: stretch; }
  .btn-touch { width: 100%; }
}
</style>
